import { FeatureFlags } from '../FeatureFlags';
import { Capabilities } from '../ServerApi/types/Capabilities';
import { Permission } from './Permission';
import { Role } from './Role';

// These role names also serve as the roles' localization keys
export const PermissionRoleNames = {
  DatasourceConnector: 'datasourceConnector',
  DatasourceEditor: 'datasourceEditor',
  Editor: 'editor',
  Interactor: 'interactor',
  ProjectLeader: 'project_leader',
  Publisher: 'publisher',
  Runner: 'runner',
  Viewer: 'viewer',

  // The following role names are for the newer permissions grid implemented in React
  Administer: 'administer',
  Explore: 'explore',
  Publish: 'publish',
  View: 'view',
};

export module PermissionTemplateRoleDefinitions {

  // Project
  const projectRoles: Role[] = [];

  const ProjectViewerAllowedCapabilities = [
    Capabilities.read
  ];
  export let ProjectViewer = Role.create(PermissionRoleNames.Viewer, ProjectViewerAllowedCapabilities, Permission.Allowed);
  projectRoles.push(ProjectViewer);

  const ProjectPublisherAllowedCapabilities = ProjectViewerAllowedCapabilities.concat([
    Capabilities.write
  ]);
  export let ProjectPublisher = Role.create(PermissionRoleNames.Publisher, ProjectPublisherAllowedCapabilities, Permission.Allowed);
  projectRoles.push(ProjectPublisher);

  const ProjectLeaderAllowedCapabilities = [
    Capabilities.projectLeader
  ];
  export let ProjectLeader = Role.create(PermissionRoleNames.ProjectLeader, ProjectLeaderAllowedCapabilities, Permission.Allowed);
  projectRoles.push(ProjectLeader);

  // Data Role
  const dataRoleRoles: Role[] = [];

  const DataRoleViewerAllowedCapabilities: string[] = [];
  export let DataRoleViewer = Role.create(PermissionRoleNames.Runner, DataRoleViewerAllowedCapabilities, Permission.Allowed);
  dataRoleRoles.push(DataRoleViewer);

  const DataRoleInteractorAllowedCapabilities = DataRoleViewerAllowedCapabilities.concat([
    Capabilities.read,
  ]);
  export let DataRoleInteractor = Role.create(PermissionRoleNames.Interactor, DataRoleInteractorAllowedCapabilities, Permission.Allowed);
  dataRoleRoles.push(DataRoleInteractor);

  const DataRoleEditorAllowedCapabilities = DataRoleInteractorAllowedCapabilities.concat([
    Capabilities.changeHierarchy,
    Capabilities.changePermissions,
    Capabilities.deleteItem,
    Capabilities.write,
  ]);
  export let DataRoleEditor = Role.create(PermissionRoleNames.Editor, DataRoleEditorAllowedCapabilities, Permission.Allowed);
  dataRoleRoles.push(DataRoleEditor);

  // Data Source
  const datasourceRoles: Role[] = [];

  const DataSourceConnectorAllowedCapabilities = [
    Capabilities.read,
    Capabilities.connect
  ];
  export let DataSourceConnector = Role.create(PermissionRoleNames.DatasourceConnector, DataSourceConnectorAllowedCapabilities, Permission.Allowed);
  datasourceRoles.push(DataSourceConnector);

  const DataSourceEditorAllowedCapabilities = DataSourceConnectorAllowedCapabilities.concat([
    Capabilities.write,
    Capabilities.deleteItem,
    Capabilities.exportXml,
    Capabilities.changePermissions,
  ]);
  export let DataSourceEditor = Role.create(PermissionRoleNames.DatasourceEditor, DataSourceEditorAllowedCapabilities, Permission.Allowed);
  datasourceRoles.push(DataSourceEditor);

  // Database
  const databaseRoles: Role[] = [];

  const DatabaseViewerAllowedCapabilities = [
    Capabilities.read,
  ];
  export let DatabaseViewer = Role.create(PermissionRoleNames.Viewer, DatabaseViewerAllowedCapabilities, Permission.Allowed);
  databaseRoles.push(DatabaseViewer);

  const DatabaseEditorAllowedCapabilities = DatabaseViewerAllowedCapabilities.concat([
    Capabilities.changePermissions,
    Capabilities.write,
  ]);
  export let DatabaseEditor = Role.create(PermissionRoleNames.Editor, DatabaseEditorAllowedCapabilities, Permission.Allowed);
  databaseRoles.push(DatabaseEditor);

  // Table
  const tableRoles: Role[] = [];

  const TableViewerAllowedCapabilities = [
    Capabilities.read,
  ];
  export let TableViewer = Role.create(PermissionRoleNames.Viewer, TableViewerAllowedCapabilities, Permission.Allowed);
  tableRoles.push(TableViewer);

  const TableEditorAllowedCapabilities = TableViewerAllowedCapabilities.concat([
    Capabilities.changePermissions,
    Capabilities.write,
  ]);
  export let TableEditor = Role.create(PermissionRoleNames.Editor, TableEditorAllowedCapabilities, Permission.Allowed);
  tableRoles.push(TableEditor);

  // Flow
  const flowRoles: Role[] = [];

  const FlowRunnerAllowedCapabilities = [
    Capabilities.execute,
    Capabilities.read,
  ];
  export let FlowRunner = Role.create(PermissionRoleNames.Runner, FlowRunnerAllowedCapabilities, Permission.Allowed);
  flowRoles.push(FlowRunner);

  const FlowEditorAllowedCapabilities = FlowRunnerAllowedCapabilities.concat([
    Capabilities.changeHierarchy,
    Capabilities.changePermissions,
    Capabilities.deleteItem,
    Capabilities.exportXml,
    Capabilities.write,
    ...(FeatureFlags.MaestroFlowEditingServerV1 ? [Capabilities.webAuthoringForFlows] : []) ,
  ]);
  export let FlowEditor = Role.create(PermissionRoleNames.Editor, FlowEditorAllowedCapabilities, Permission.Allowed);
  flowRoles.push(FlowEditor);

  // Workbook
  const workbookRoles: Role[] = [];

  const WorkbookViewerAllowedCapabilities = [
    Capabilities.addComment,
    Capabilities.exportData,
    Capabilities.exportImage,
    Capabilities.read,
    Capabilities.viewComments
  ];
  export let WorkbookViewer = Role.create(PermissionRoleNames.Viewer, WorkbookViewerAllowedCapabilities, Permission.Allowed);
  workbookRoles.push(WorkbookViewer);

  const WorkbookInteractorAllowedCapabilities = WorkbookViewerAllowedCapabilities.concat([
    Capabilities.filter,
    Capabilities.shareView,
    Capabilities.vud,
    Capabilities.webAuthoring
  ]);
  export let WorkbookInteractor = Role.create(PermissionRoleNames.Interactor, WorkbookInteractorAllowedCapabilities, Permission.Allowed);
  workbookRoles.push(WorkbookInteractor);

  const WorkbookEditorAllowedCapabilities = WorkbookInteractorAllowedCapabilities.concat([
    Capabilities.changeHierarchy,
    Capabilities.changePermissions,
    Capabilities.deleteItem,
    Capabilities.exportXml,
    Capabilities.write
  ]);
  export let WorkbookEditor = Role.create(PermissionRoleNames.Editor, WorkbookEditorAllowedCapabilities, Permission.Allowed);
  workbookRoles.push(WorkbookEditor);


  // View
  const viewRoles: Role[] = [];

  const ViewViewerAllowedCapabilities = [
    Capabilities.addComment,
    Capabilities.exportData,
    Capabilities.exportImage,
    Capabilities.read,
    Capabilities.viewComments
  ];
  export let ViewViewer = Role.create(PermissionRoleNames.Viewer, ViewViewerAllowedCapabilities, Permission.Allowed);
  viewRoles.push(ViewViewer);

  const ViewInteractorAllowedCapabilities = ViewViewerAllowedCapabilities.concat([
    Capabilities.filter,
    Capabilities.shareView,
    Capabilities.vud,
    Capabilities.webAuthoring
  ]);
  export let ViewInteractor = Role.create(PermissionRoleNames.Interactor, ViewInteractorAllowedCapabilities, Permission.Allowed);
  viewRoles.push(ViewInteractor);

  const ViewEditorAllowedCapabilities = ViewInteractorAllowedCapabilities.concat([
    Capabilities.changePermissions,
    Capabilities.deleteItem
  ]);
  export let ViewEditor = Role.create(PermissionRoleNames.Editor, ViewEditorAllowedCapabilities, Permission.Allowed);
  viewRoles.push(ViewEditor);

  // The following roles are for the newer permissions grid implemented in React
  // Need to be in sync with app/permissions/SectionGroups.ts

  // Database
  const DatabaseViewRoleAllowedCapabilities = [Capabilities.read];
  const DatabasePublishRoleAllowedCapabilities = [...DatabaseViewRoleAllowedCapabilities, Capabilities.write];
  const DatabaseAdministerRoleAllowedCapabilities = [...DatabasePublishRoleAllowedCapabilities, Capabilities.changePermissions];

  let databaseRolesReact: Role[] = [];
  databaseRolesReact.push(Role.create(PermissionRoleNames.View, DatabaseViewRoleAllowedCapabilities, Permission.Allowed));
  databaseRolesReact.push(Role.create(PermissionRoleNames.Publish, DatabasePublishRoleAllowedCapabilities, Permission.Allowed));
  databaseRolesReact.push(Role.create(PermissionRoleNames.Administer, DatabaseAdministerRoleAllowedCapabilities, Permission.Allowed));

  // DataRole
  const DataRoleViewRoleAllowedCapabilities = [Capabilities.read];
  const DataRolePublishRoleAllowedCapabilities = [...DataRoleViewRoleAllowedCapabilities, Capabilities.write];
  const DataRoleAdministerRoleAllowedCapabilities = [...DataRolePublishRoleAllowedCapabilities, Capabilities.changeHierarchy, Capabilities.changePermissions, Capabilities.deleteItem];

  let dataRoleRolesReact: Role[] = [];
  dataRoleRolesReact.push(Role.create(PermissionRoleNames.View, DataRoleViewRoleAllowedCapabilities, Permission.Allowed));
  dataRoleRolesReact.push(Role.create(PermissionRoleNames.Publish, DataRolePublishRoleAllowedCapabilities, Permission.Allowed));
  dataRoleRolesReact.push(Role.create(PermissionRoleNames.Administer, DataRoleAdministerRoleAllowedCapabilities, Permission.Allowed));

  // Datasource
  const DatasourceViewRoleAllowedCapabilities = [Capabilities.read, Capabilities.connect];
  const DatasourceExploreRoleAllowedCapabilities = [...DatasourceViewRoleAllowedCapabilities, Capabilities.exportXml];
  const DatasourcePublishRoleAllowedCapabilities = [...DatasourceExploreRoleAllowedCapabilities, Capabilities.write];
  const DatasourceAdministerRoleAllowedCapabilities = [...DatasourcePublishRoleAllowedCapabilities, Capabilities.changePermissions, Capabilities.deleteItem];

  let datasourceRolesReact: Role[] = [];
  datasourceRolesReact.push(Role.create(PermissionRoleNames.View, DatasourceViewRoleAllowedCapabilities, Permission.Allowed));
  datasourceRolesReact.push(Role.create(PermissionRoleNames.Explore, DatasourceExploreRoleAllowedCapabilities, Permission.Allowed));
  datasourceRolesReact.push(Role.create(PermissionRoleNames.Publish, DatasourcePublishRoleAllowedCapabilities, Permission.Allowed));
  datasourceRolesReact.push(Role.create(PermissionRoleNames.Administer, DatasourceAdministerRoleAllowedCapabilities, Permission.Allowed));

  // Flow
  const FlowViewRoleAllowedCapabilities = [Capabilities.read];
  const FlowExploreRoleAllowedCapabilities = [...FlowViewRoleAllowedCapabilities, Capabilities.exportXml];
  const FlowPublishRoleAllowedCapabilities = [...FlowExploreRoleAllowedCapabilities, Capabilities.execute, Capabilities.write];
  const FlowAdministerRoleAllowedCapabilities = [...FlowPublishRoleAllowedCapabilities, Capabilities.changeHierarchy, Capabilities.changePermissions, Capabilities.deleteItem];

  let flowRolesReact: Role[] = [];
  flowRolesReact.push(Role.create(PermissionRoleNames.View, FlowViewRoleAllowedCapabilities, Permission.Allowed));
  flowRolesReact.push(Role.create(PermissionRoleNames.Explore, FlowExploreRoleAllowedCapabilities, Permission.Allowed));
  flowRolesReact.push(Role.create(PermissionRoleNames.Publish, FlowPublishRoleAllowedCapabilities, Permission.Allowed));
  flowRolesReact.push(Role.create(PermissionRoleNames.Administer, FlowAdministerRoleAllowedCapabilities, Permission.Allowed));

  // Metric
  const MetricViewRoleAllowedCapabilities = [Capabilities.read];
  const MetricPublishRoleAllowedCapabilities = [...MetricViewRoleAllowedCapabilities, Capabilities.write];
  const MetricAdministerRoleAllowedCapabilities = [...MetricPublishRoleAllowedCapabilities, Capabilities.changeHierarchy, Capabilities.changePermissions, Capabilities.deleteItem];

  let metricRolesReact: Role[] = [];
  metricRolesReact.push(Role.create(PermissionRoleNames.View, MetricViewRoleAllowedCapabilities, Permission.Allowed));
  metricRolesReact.push(Role.create(PermissionRoleNames.Publish, MetricPublishRoleAllowedCapabilities, Permission.Allowed));
  metricRolesReact.push(Role.create(PermissionRoleNames.Administer, MetricAdministerRoleAllowedCapabilities, Permission.Allowed));

  // Project
  const ProjectViewRoleAllowedCapabilities = [Capabilities.read];
  const ProjectPublishRoleAllowedCapabilities = [...ProjectViewRoleAllowedCapabilities, Capabilities.write];

  let projectRolesReact: Role[] = [];
  projectRolesReact.push(Role.create(PermissionRoleNames.View, ProjectViewRoleAllowedCapabilities, Permission.Allowed));
  projectRolesReact.push(Role.create(PermissionRoleNames.Publish, ProjectPublishRoleAllowedCapabilities, Permission.Allowed));

  // Table
  const TableViewRoleAllowedCapabilities = [Capabilities.read];
  const TablePublishRoleAllowedCapabilities = [...TableViewRoleAllowedCapabilities, Capabilities.write];
  const TableAdministerRoleAllowedCapabilities = [...TablePublishRoleAllowedCapabilities, Capabilities.changePermissions];

  let tableRolesReact: Role[] = [];
  tableRolesReact.push(Role.create(PermissionRoleNames.View, TableViewRoleAllowedCapabilities, Permission.Allowed));
  tableRolesReact.push(Role.create(PermissionRoleNames.Publish, TablePublishRoleAllowedCapabilities, Permission.Allowed));
  tableRolesReact.push(Role.create(PermissionRoleNames.Administer, TableAdministerRoleAllowedCapabilities, Permission.Allowed));

  // View
  const ViewViewRoleAllowedCapabilities = [Capabilities.addComment, Capabilities.exportData, Capabilities.exportImage, Capabilities.filter, Capabilities.read, Capabilities.viewComments];
  const ViewExploreRoleAllowedCapabilities = [...ViewViewRoleAllowedCapabilities, Capabilities.shareView, Capabilities.vud, Capabilities.webAuthoring];
  const ViewAdministerRoleAllowedCapabilities = [...ViewExploreRoleAllowedCapabilities, Capabilities.changePermissions, Capabilities.deleteItem];

  let viewRolesReact: Role[] = [];
  viewRolesReact.push(Role.create(PermissionRoleNames.View, ViewViewRoleAllowedCapabilities, Permission.Allowed));
  viewRolesReact.push(Role.create(PermissionRoleNames.Explore, ViewExploreRoleAllowedCapabilities, Permission.Allowed));
  viewRolesReact.push(Role.create(PermissionRoleNames.Administer, ViewAdministerRoleAllowedCapabilities, Permission.Allowed));

  // Workbook
  const WorkbookViewRoleAllowedCapabilities = [Capabilities.addComment, Capabilities.exportData, Capabilities.exportImage, Capabilities.filter, Capabilities.read, Capabilities.viewComments];
  const WorkbookExploreRoleAllowedCapabilities = [...WorkbookViewRoleAllowedCapabilities, Capabilities.shareView, Capabilities.vud, Capabilities.webAuthoring];
  const WorkbookPublishRoleAllowedCapabilities = [...WorkbookExploreRoleAllowedCapabilities, Capabilities.exportXml, Capabilities.write];
  const WorkbookAdministerRoleAllowedCapabilities = [...WorkbookPublishRoleAllowedCapabilities, Capabilities.changeHierarchy, Capabilities.changePermissions, Capabilities.deleteItem];

  let workbookRolesReact: Role[] = [];
  workbookRolesReact.push(Role.create(PermissionRoleNames.View, WorkbookViewRoleAllowedCapabilities, Permission.Allowed));
  workbookRolesReact.push(Role.create(PermissionRoleNames.Explore, WorkbookExploreRoleAllowedCapabilities, Permission.Allowed));
  workbookRolesReact.push(Role.create(PermissionRoleNames.Publish, WorkbookPublishRoleAllowedCapabilities, Permission.Allowed));
  workbookRolesReact.push(Role.create(PermissionRoleNames.Administer, WorkbookAdministerRoleAllowedCapabilities, Permission.Allowed));

  // For the newer permissions grid implemented in React 
  export let explicitRolesByContentTypeReact: _.Dictionary<Role[]> = {
    database: databaseRolesReact,
    databaseTableTemplate: tableRolesReact,
    dataRole: dataRoleRolesReact,
    datasource: datasourceRolesReact,
    flow: flowRolesReact,
    metric: metricRolesReact,
    project: projectRolesReact,
    projectDataRoleTemplate: dataRoleRolesReact,
    projectDatasourceTemplate: datasourceRolesReact,
    projectFlowTemplate: flowRolesReact,
    projectMetricTemplate: metricRolesReact,
    projectWorkbookTemplate: workbookRolesReact,
    table: tableRolesReact,
    view: viewRolesReact,
    workbook: workbookRolesReact,
  };

  // Below is for the old permissions grid implemented in Angular
  // All the roles get added onto this object
  export let explicitRolesByContentType: _.Dictionary<Role[]> = {
    dataRole: dataRoleRoles,
    datasource: datasourceRoles,
    flow: flowRoles,
    project: projectRoles,
    projectDataRoleTemplate: dataRoleRoles,
    projectDatasourceTemplate: datasourceRoles,
    projectFlowTemplate: flowRoles,
    projectWorkbookTemplate: workbookRoles,
    database: databaseRoles,
    databaseTableTemplate: tableRoles,
    table: tableRoles,
    view: viewRoles,
    workbook: workbookRoles,
  };

}
